<template>
  <div ref="root" class="nav-bell-wrap">
    <button
      type="button"
      class="nav-bell"
      :class="{ open }"
      :aria-label="unseen > 0 ? `Notifications, ${unseen} new` : 'Notifications'"
      :aria-expanded="open ? 'true' : 'false'"
      title="Notifications"
      @click="toggle"
    >
      <font-awesome-icon icon="fa-regular fa-bell" class="fa" />
      <span v-if="unseen > 0" class="nav-bell-badge">{{ unseen > 99 ? '99+' : unseen }}</span>
    </button>

    <transition name="nav-bell-fade">
      <div v-if="open" class="nav-bell-panel" role="dialog" aria-label="Notifications">
        <div class="nav-bell-head">
          <span class="nav-bell-title">Notifications</span>
          <button v-if="unreadCount > 0" type="button" class="chat-inline-link" @click="markAllRead">Mark all as read</button>
        </div>

        <div v-if="desktopOffer" class="chat-banner">
          <i class="pi pi-desktop"></i>
          <span class="chat-banner-text">Get a desktop alert when a message arrives while you are on another tab.</span>
          <span class="chat-banner-actions">
            <button type="button" class="btn btn-custom-primary btn-sm" @click="enableDesktop">Enable</button>
            <button type="button" class="chat-inline-link" @click="dismissDesktop">Not now</button>
          </span>
        </div>

        <div class="nav-bell-list">
          <div v-if="!loaded" class="nav-bell-empty">Loading…</div>
          <div v-else-if="!notifications.length" class="nav-bell-empty">
            <i class="pi pi-check-circle"></i>
            <span>You're all caught up.</span>
          </div>
          <button
            v-for="entry in notifications"
            :key="entry.id"
            type="button"
            :class="['nav-bell-item', { unread: !entry.isRead, mention: entry.kind === 'mention' }]"
            @click="openNotification(entry)"
          >
            <span class="nav-bell-avatar">
              <chatAvatar :employee="entry.actor" :size="36" />
              <span v-if="entry.kind === 'mention'" class="nav-bell-kind mention" title="You were mentioned">@</span>
              <span v-else-if="entry.kind === 'added'" class="nav-bell-kind added" title="Added to a group"><i class="pi pi-user-plus"></i></span>
              <span v-else-if="entry.conversationType === 'channel'" class="nav-bell-kind channel" title="Channel"><i class="pi pi-hashtag"></i></span>
              <span v-else-if="entry.conversationType === 'group'" class="nav-bell-kind group" title="Group"><i class="pi pi-users"></i></span>
            </span>
            <span class="nav-bell-main">
              <span class="nav-bell-item-title">{{ entry.title }}</span>
              <span class="nav-bell-item-body">{{ bodyFor(entry) }}</span>
              <span class="nav-bell-item-time">{{ when(entry) }}</span>
            </span>
            <span v-if="!entry.isRead" class="nav-bell-dot" aria-label="Unread"></span>
          </button>
        </div>

        <div class="nav-bell-foot">
          <router-link :to="{ name: CHAT_ROUTE }" class="chat-inline-link" @click="open = false"><i class="pi pi-comments"></i> Open chat</router-link>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBell } from '@fortawesome/free-regular-svg-icons';
import chatAvatar from '@/components/chat/Chat_Avatar.vue';
import { CHAT_CONVERSATION_ROUTE, CHAT_ROUTE } from '@/router/routes';
import { GET_CHAT_NOTIFICATIONS, GET_CHAT_UNREAD, MARK_CHAT_NOTIFICATIONS_READ, MARK_CHAT_NOTIFICATIONS_SEEN } from '@/store/action_types';
import { relativeLabel } from '@/utils/chat';
import { dismissDesktopOffer, requestDesktopPermission, shouldOfferDesktopNotifications } from '@/utils/chat_alerts';

library.add(faBell);

/**
 * The bell in the navbar. The badge counts entries not yet seen; opening the panel
 * clears it. Entries stay marked unread until they are opened (or "Mark all as read").
 */
export default {
  name: 'NotificationBell',
  components: { chatAvatar },
  data() {
    return {
      CHAT_ROUTE,
      open: false,
      desktopOffer: false,
    };
  },
  computed: {
    notifications() {
      return this.$store.getters.chatNotifications;
    },
    loaded() {
      return this.$store.getters.chatNotificationsLoaded;
    },
    unseen() {
      return this.$store.getters.chatUnseenCount;
    },
    unreadCount() {
      return this.$store.getters.chatUnreadNotificationCount;
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.desktopOffer = shouldOfferDesktopNotifications();
        this.$store.dispatch(GET_CHAT_NOTIFICATIONS);
        this.$store.dispatch(MARK_CHAT_NOTIFICATIONS_SEEN);
      }
    },
    bodyFor(entry) {
      const body = entry.body ?? '';
      return entry.messageCount > 1 ? `${entry.messageCount} new messages · ${body}` : body;
    },
    when(entry) {
      return relativeLabel(entry.updatedAt);
    },
    openNotification(entry) {
      if (!entry.isRead) this.$store.dispatch(MARK_CHAT_NOTIFICATIONS_READ, { ids: [entry.id] });
      this.open = false;
      const current = this.$route;
      if (current.name === CHAT_CONVERSATION_ROUTE && current.params.id === entry.conversationId) return;
      this.$router.push({ name: CHAT_CONVERSATION_ROUTE, params: { id: entry.conversationId } });
    },
    markAllRead() {
      this.$store.dispatch(MARK_CHAT_NOTIFICATIONS_READ, { all: true });
    },
    async enableDesktop() {
      await requestDesktopPermission();
      this.desktopOffer = shouldOfferDesktopNotifications();
    },
    dismissDesktop() {
      dismissDesktopOffer();
      this.desktopOffer = false;
    },
    onDocumentClick(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) this.open = false;
    },
    onKey(event) {
      if (event.key === 'Escape') this.open = false;
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKey);
    this.$store.dispatch(GET_CHAT_UNREAD);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKey);
  },
};
</script>
